import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, X, Upload, Image, Plus } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ProjectStatus } from '@/types/project';
import { useAccessibleProjectTypes, useAccessibleProjectGroups } from '@/hooks/useAccessibleFilterOptions';
import { useAccessibleContractYears } from '@/hooks/useAccessibleContractYears';
import { useCustomGroups } from '@/hooks/useCustomGroups';
import { toast } from 'sonner';

interface ProjectAddDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (project: { project_name: string; image_file: File | null; status: ProjectStatus; project_type: string; project_group: string; contract_year?: string; company?: string }) => void;
  isLoading?: boolean;
}

export const ProjectAddDialog = ({ isOpen, onClose, onSubmit, isLoading }: ProjectAddDialogProps) => {
  const [formData, setFormData] = useState({
    project_name: '',
    image_file: null as File | null,
    status: 'intake' as ProjectStatus,
    project_type: '',
    project_group: '',
    contract_year: '',
    company: 'none' // ENP or Solid or none
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  
  // Get project types and groups from database
  const { data: projectTypes, isLoading: projectTypesLoading } = useAccessibleProjectTypes();
  const { data: projectGroups, isLoading: projectGroupsLoading } = useAccessibleProjectGroups();
  const { data: contractYears, isLoading: contractYearsLoading } = useAccessibleContractYears();
  
  // Use unified custom groups hook
  const { customGroups, addGroup, groupNameExists } = useCustomGroups();
  
  // New group creation state
  const [showNewGroupForm, setShowNewGroupForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  
  // Combine database groups and custom groups
  const allGroups = [
    ...projectGroups.map(name => ({ id: name, name, color: undefined })),
    ...customGroups
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.project_name.trim()) {
      const submitData = {
        ...formData,
        company: formData.company === 'none' ? '' : formData.company
      };
      onSubmit(submitData);
      setFormData({ project_name: '', image_file: null, status: 'intake', project_type: '', project_group: '', contract_year: '', company: 'none' });
      setImagePreview(null);
    }
  };

  const handleClose = () => {
    setFormData({ project_name: '', image_file: null, status: 'intake', project_type: '', project_group: '', contract_year: '', company: 'none' });
    setImagePreview(null);
    setShowNewGroupForm(false);
    setNewGroupName('');
    onClose();
  };

  const handleAddGroup = () => {
    const trimmedName = newGroupName.trim();
    
    if (!trimmedName) {
      toast.error("Tên nhóm không được để trống");
      return;
    }
    
    // Check for duplicate names (including database groups)
    if (groupNameExists(trimmedName) || projectGroups.includes(trimmedName)) {
      toast.error("Tên nhóm đã tồn tại");
      return;
    }
    
    // Add new custom group using hook
    const newGroup = addGroup({
      name: trimmedName,
      description: 'Nhóm tùy chỉnh',
      color: '#6366f1'
    });
    
    if (newGroup) {
      setFormData(prev => ({ ...prev, project_group: trimmedName }));
      setNewGroupName('');
      setShowNewGroupForm(false);
      toast.success(`Đã thêm nhóm "${trimmedName}"`);
    } else {
      toast.error("Không thể thêm nhóm mới");
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, image_file: file }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setFormData(prev => ({ ...prev, image_file: null }));
      setImagePreview(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-full max-w-md mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Thêm Dự Án Mới</DialogTitle>
          <DialogDescription>
            Tạo dự án mới với tên và hình ảnh đại diện.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          <div className="space-y-2">
            <Label htmlFor="project_name">Tên Dự Án *</Label>
            <Input
              id="project_name"
              value={formData.project_name}
              onChange={(e) => setFormData(prev => ({ ...prev, project_name: e.target.value }))}
              placeholder="Nhập tên dự án"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="status">Trạng thái</Label>
            <Select
              value={formData.status}
              onValueChange={(value: ProjectStatus) => 
                setFormData(prev => ({ ...prev, status: value }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="intake">Tiếp nhận</SelectItem>
                <SelectItem value="tender">Đấu thầu</SelectItem>
                <SelectItem value="build">Thi công</SelectItem>
                <SelectItem value="defects">Sửa chữa</SelectItem>
                <SelectItem value="archive">Lưu trữ</SelectItem>
                <SelectItem value="service">Bảo hành</SelectItem>
                <SelectItem value="inactive">Ngừng hoạt động</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="project_type">Loại dự án</Label>
            <Select
              value={formData.project_type}
              onValueChange={(value: string) => 
                setFormData(prev => ({ ...prev, project_type: value }))
              }
              disabled={projectTypesLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder={projectTypesLoading ? "Đang tải..." : "Chọn loại dự án"} />
              </SelectTrigger>
              <SelectContent>
                {projectTypes.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="project_group">Nhóm dự án</Label>
            <div className="space-y-2">
              <Select
                value={formData.project_group}
                onValueChange={(value: string) => 
                  setFormData(prev => ({ ...prev, project_group: value }))
                }
                disabled={projectGroupsLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder={projectGroupsLoading ? "Đang tải..." : "Chọn nhóm dự án"} />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {allGroups.map((group) => (
                    <SelectItem key={group.id} value={group.name}>
                      <div className="flex items-center gap-2">
                        {group.color && (
                          <div 
                            className="w-3 h-3 rounded-full flex-shrink-0" 
                            style={{ backgroundColor: group.color }}
                          />
                        )}
                        <span>{group.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {!showNewGroupForm ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowNewGroupForm(true)}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Tạo nhóm mới
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Input
                    placeholder="Tên nhóm mới"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddGroup();
                      }
                      if (e.key === 'Escape') {
                        setShowNewGroupForm(false);
                        setNewGroupName('');
                      }
                    }}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAddGroup}
                    disabled={!newGroupName.trim()}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowNewGroupForm(false);
                      setNewGroupName('');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contract_year">Năm hợp đồng</Label>
            <Select
              value={formData.contract_year}
              onValueChange={(value: string) =>
                setFormData(prev => ({ ...prev, contract_year: value }))
              }
              disabled={contractYearsLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder={contractYearsLoading ? "Đang tải..." : "Chọn năm hợp đồng"} />
              </SelectTrigger>
              <SelectContent>
                {contractYears.map((year) => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="company">Công ty</Label>
            <Select
              value={formData.company}
              onValueChange={(value: string) => 
                setFormData(prev => ({ ...prev, company: value }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Chọn công ty" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Không chọn</SelectItem>
                <SelectItem value="ENP">ENP</SelectItem>
                <SelectItem value="Solid">Solid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="image_file">Hình Ảnh Dự Án</Label>
            <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
              <Input
                id="image_file"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleFileChange}
                className="flex-1"
              />
              {imagePreview && (
                <div className="w-12 h-12 sm:w-16 sm:h-16 border rounded-lg overflow-hidden flex-shrink-0">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              {!imagePreview && (
                <div className="w-12 h-12 sm:w-16 sm:h-16 border border-dashed rounded-lg flex items-center justify-center flex-shrink-0">
                  <Image className="h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground" />
                </div>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Chỉ chấp nhận file JPG, PNG, WebP. Tối đa 5MB.
            </p>
          </div>
          <div className="flex flex-col gap-2 pt-2 sm:pt-4 sm:flex-row">
            <Button 
              type="submit"
              disabled={!formData.project_name.trim() || isLoading}
              className="flex-1"
            >
              <Save className="h-4 w-4 mr-2" />
              {isLoading ? 'Đang lưu...' : 'Lưu'}
            </Button>
            <Button type="button" variant="outline" onClick={handleClose} className="flex-1 sm:flex-none">
              <X className="h-4 w-4 mr-2" />
              Hủy
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};